import React, { useState, useEffect } from 'react'
import { useToken } from '@hub/design-system-base'
import Color from 'color'
import { Box } from '@hub/box'
import { Portal } from '@hub/portal'
import { deburr, kebabCase, replace, delay } from 'lodash'
import { Link } from '@hub/link'
import { LinkItem } from '../types'
import { MegaMenu } from './megamenu'
import { MainNavItemWrapper } from './main-nav-item-wrapper'

interface MegaMenuNavItemProps {
  item: LinkItem
  megaMenuPortalRef?: React.RefObject<HTMLElement | null>
}

export const MegaMenuNavItem: React.FC<MegaMenuNavItemProps> = ({
  item,
  megaMenuPortalRef,
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const [overlayTop, setOverlayTop] = useState(0)
  const bounceRef = React.useRef(0)
  const focusRef = React.useRef(false)
  const megamenuFocusRef = React.useRef(false)
  const OVERLAY_OFFSET = 1

  const itemId = kebabCase(replace(deburr(item.label), /([^A-Za-z])/, ''))

  // For some reason I can't figure out when first loaded by SSR the event is triggered twice
  // To get around this I wanted to use _.debounce but because we still need to stop propegation on that second call
  // I'm tracking time myself to prevent the second call from happening
  const open: React.MouseEventHandler = event => {
    const MINIMUM_TIME_BETWEEN_EVENTS = 300
    const bounced =
      event.timeStamp - bounceRef.current < MINIMUM_TIME_BETWEEN_EVENTS
    if (isOpen && !bounced) {
      bounceRef.current = event.timeStamp
      setIsOpen(false)
    } else {
      event.preventDefault()
      event.stopPropagation()
      bounceRef.current = event.timeStamp
      setIsOpen(true)
    }
  }

  const close: React.MouseEventHandler = _event => {
    setIsOpen(false)
  }

  const handleKeyDown: React.KeyboardEventHandler = event => {
    if (event.key === 'Enter') {
      focusRef.current = false
      megamenuFocusRef.current = true
    }
  }

  useEffect(() => {
    if (!megaMenuPortalRef?.current) {
      return
    }

    setOverlayTop(
      // Adding 1px to push the overlay down a little to be safe otherwise it may add a gray line at the edge
      megaMenuPortalRef?.current?.getBoundingClientRect().bottom -
        (document?.querySelector('body')?.getBoundingClientRect()?.top || 0) +
        OVERLAY_OFFSET
    )
  }, [isOpen, megaMenuPortalRef])

  const OverlayBackgroundColor = Color(useToken('colors', 'licorice'))
    .alpha(0.8) // eslint-disable-line no-magic-numbers
    .rgb()
    .string()

  return (
    <>
      <MainNavItemWrapper
        isDisplayedHorizontally
        ref={listItem => {
          if (listItem && focusRef.current) {
            listItem.querySelector('a')?.focus()
            focusRef.current = false
          }
        }}
        onMouseEnter={open}
        onMouseLeave={close}
        onFocus={() => (focusRef.current = true)}
        onBlur={() => {
          focusRef.current = false
        }}
        onKeyDown={handleKeyDown}
      >
        <Link
          href={item.url}
          hasUnderline={false}
          aria-expanded={isOpen}
          aria-controls={`${itemId}`}
          sx={{
            height: 'size-full',
            display: 'inline-flex',
            alignItems: 'center',
            _hover: {
              textDecoration: 'none',
            },
          }}
          onClick={open}
        >
          {item.label}
        </Link>
        <Portal containerRef={megaMenuPortalRef}>
          {isOpen && (
            <Box
              sx={{
                position: 'absolute',
                left: '50%',
                right: '50%',
                top: 'calc(100% + 1px)',
                backgroundColor: 'white',
                width: '100vw',
                marginRight: '-50vw',
                marginLeft: '-50vw',
                zIndex: 'dropdown',
              }}
              role="none"
              ref={(box: HTMLElement) => {
                if (box && megamenuFocusRef.current) {
                  const DELAY_FOR_MAKING_SURE_FOCUS_IS_POSSIBLE = 50
                  delay(
                    () => box.querySelector('a')?.focus(),
                    DELAY_FOR_MAKING_SURE_FOCUS_IS_POSSIBLE
                  )
                  megamenuFocusRef.current = false
                }
              }}
            >
              <MegaMenu itemId={itemId} megaMenuItems={item.megaMenuItems} />
            </Box>
          )}
        </Portal>
      </MainNavItemWrapper>
      {isOpen && (
        <Portal>
          <Box
            sx={{
              position: 'absolute',
              backgroundColor: OverlayBackgroundColor,
              top: overlayTop,
              bottom: 0,
              left: 0,
              right: 0,
            }}
            onMouseEnter={() => setIsOpen(false)}
          />
        </Portal>
      )}
    </>
  )
}
